import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Users } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

import { apiClient } from "../lib/api";
import { authClient, useAuthSession } from "../lib/auth-client";

export const Route = createFileRoute("/invite/$token")({
  component: InvitePage,
});

const normalizeEmail = (email: string) => email.trim().toLowerCase();

function InvitePage() {
  const { token } = Route.useParams();
  const router = useRouter();
  const { data: session, isPending: sessionPending } = useAuthSession();
  const isLoggedIn = !!session?.user;

  const { data: invite, isPending: invitePending, error } = useQuery({
    queryKey: ["circle-invite", token],
    queryFn: () => apiClient.getCircleInviteInfo(token),
  });

  const accept = useMutation({
    mutationFn: () => apiClient.acceptCircleInvite(token),
    onSuccess: () => void router.navigate({ to: "/circle" }),
  });

  const sessionEmail = session?.user.email ?? null;
  const isInviteeSignedIn = !!sessionEmail
    && !!invite
    && normalizeEmail(sessionEmail) === normalizeEmail(invite.inviteeEmail);

  async function handleSwitchAccount() {
    await authClient.signOut();
    await router.navigate({ to: "/login", search: { redirect: `/invite/${token}` } });
  }

  useEffect(() => {
    if (!invite || !isInviteeSignedIn || accept.isPending || accept.isSuccess) {
      return;
    }

    if (invite.status !== "PENDING") {
      return;
    }

    accept.mutate();
  }, [accept, invite, isInviteeSignedIn]);

  if (invitePending || sessionPending) {
    return <InviteShell><p className="text-sm text-muted-foreground">Loading...</p></InviteShell>;
  }

  if (error || !invite) {
    return (
      <InviteShell>
        <p className="text-sm text-destructive">This invite link is invalid or has expired.</p>
      </InviteShell>
    );
  }

  if (invite.status === "ACCEPTED") {
    return (
      <InviteShell>
        <p className="text-sm text-muted-foreground">This invite has already been accepted.</p>
        {isLoggedIn && (
          <Button className="mt-4" onClick={() => void router.navigate({ to: "/circle" })}>
            Go to Circle
          </Button>
        )}
      </InviteShell>
    );
  }

  if (invite.status === "EXPIRED" || invite.status === "REVOKED") {
    return (
      <InviteShell>
        <p className="text-sm text-destructive">This invite has expired or been revoked.</p>
      </InviteShell>
    );
  }

  return (
    <InviteShell>
      <Card className="w-full max-w-sm">
        <CardHeader className="pb-4 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
            <Users className="h-5 w-5 text-muted-foreground" />
          </div>
          <CardTitle className="text-lg">You've been invited</CardTitle>
          <CardDescription>
            <span className="font-medium text-foreground">{invite.inviterName}</span> invited you to join{" "}
            <span className="font-medium text-foreground">{invite.circleName}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-center text-sm text-muted-foreground">
            This invite is for <span className="font-medium text-foreground">{invite.inviteeEmail}</span>.
          </p>
          {isLoggedIn ? (
            isInviteeSignedIn ? (
              <>
                {accept.error && (
                  <p className="text-sm text-destructive">
                    {accept.error instanceof Error ? accept.error.message : "Something went wrong."}
                  </p>
                )}
                <Button className="w-full" onClick={() => accept.mutate()} disabled={accept.isPending}>
                  {accept.isPending ? "Joining..." : "Accept invite"}
                </Button>
              </>
            ) : (
              <>
                <p className="text-center text-sm text-destructive">
                  You're signed in as <span className="font-medium">{sessionEmail}</span>. Switch to{" "}
                  <span className="font-medium">{invite.inviteeEmail}</span> to accept this invite.
                </p>
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => void handleSwitchAccount()}
                >
                  Switch account
                </Button>
              </>
            )
          ) : (
            <>
              <p className="text-center text-sm text-muted-foreground">
                Sign in as {invite.inviteeEmail} to accept this invite
              </p>
              <Button
                className="w-full"
                onClick={() => void router.navigate({ to: "/login", search: { redirect: `/invite/${token}` } })}
              >
                Sign in
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </InviteShell>
  );
}

function InviteShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-4">
      <div className="mb-2 flex items-center gap-2">
        <div className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-foreground">
          <div className="h-1.5 w-1.5 rounded-full bg-foreground" />
        </div>
        <span className="text-sm font-semibold tracking-tight">Consistency Circle</span>
      </div>
      {children}
    </div>
  );
}
